import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export function usePWATracking() {
  const { user } = useAuth();
  const sessionIdRef = useRef<string | null>(null);
  const isOfflineRef = useRef(false);
  const pageViewCountRef = useRef(0);
  const calculatorCountRef = useRef(0);

  // Track PWA install
  useEffect(() => {
    const handleAppInstalled = async () => {
      try {
        await supabase.from('pwa_installs').insert({
          user_id: user?.id || null,
          user_agent: navigator.userAgent,
          platform: navigator.platform,
          device_type: /mobile|android|iphone|ipad/i.test(navigator.userAgent) ? 'mobile' : 'desktop'
        });
      } catch (error) {
        console.error('Error tracking PWA install:', error);
      }
    };

    window.addEventListener('appinstalled', handleAppInstalled);
    return () => window.removeEventListener('appinstalled', handleAppInstalled);
  }, [user]);

  // Track session and offline usage
  useEffect(() => {
    const startSession = async () => {
      try {
        const { data, error } = await supabase
          .from('pwa_sessions')
          .insert({
            user_id: user?.id || null,
            is_offline: !navigator.onLine,
            user_agent: navigator.userAgent,
            page_views: 0,
            calculators_used: 0
          })
          .select()
          .single();

        if (!error && data) {
          sessionIdRef.current = data.id;
        }
      } catch (error) {
        console.error('Error starting session:', error);
      }
    };

    const endSession = async () => {
      if (sessionIdRef.current) {
        try {
          await supabase
            .from('pwa_sessions')
            .update({
              session_end: new Date().toISOString(),
              page_views: pageViewCountRef.current,
              calculators_used: calculatorCountRef.current
            })
            .eq('id', sessionIdRef.current);
        } catch (error) {
          console.error('Error ending session:', error);
        }
      }
    };

    startSession();

    // Track online/offline status
    const handleOnline = async () => {
      isOfflineRef.current = false;
      if (sessionIdRef.current) {
        await supabase
          .from('pwa_sessions')
          .update({ is_offline: false })
          .eq('id', sessionIdRef.current);
      }
    };

    const handleOffline = async () => {
      isOfflineRef.current = true;
      if (sessionIdRef.current) {
        await supabase
          .from('pwa_sessions')
          .update({ is_offline: true })
          .eq('id', sessionIdRef.current);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('beforeunload', endSession);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('beforeunload', endSession);
      endSession();
    };
  }, [user]);

  // Track page views
  const trackPageView = () => {
    pageViewCountRef.current += 1;
  };

  // Track calculator usage
  const trackCalculatorUse = () => {
    calculatorCountRef.current += 1;
  };

  return {
    trackPageView,
    trackCalculatorUse
  };
}
